import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getProductsBySeller, filterAvailableProducts } from '../utils/productApi';
import { getTaxonomyForFilters } from '../utils/taxonomyApi';

export function useProducts() {
  const { currentUser } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taxonomyData, setTaxonomyData] = useState({
    listingType: [],
    itemCategory: [],
    cuisine: [],
    courseType: [],
    dishType: [],
    dietaryTags: []
  });
  const [filters, setFilters] = useState({
    status: 'all',
    category: '',
    subCategory: '',
    search: ''
  });
  const [sortBy, setSortBy] = useState('name');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch taxonomy data from Firestore
  const fetchTaxonomyData = useCallback(async () => {
    try {
      const taxonomy = await getTaxonomyForFilters();
      setTaxonomyData(taxonomy);
    } catch (err) {
      console.error('Error fetching taxonomy data:', err);
      // Don't set error for taxonomy, just use empty arrays
    }
  }, []);

  // Fetch products from Firebase
  const fetchProducts = useCallback(async () => {
    if (!currentUser?.uid) {
      setProducts([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const productsData = await getProductsBySeller(currentUser.uid);
      // Fetched products from API
      
      // If no products found, use sample data for testing
      if (!productsData || productsData.length === 0) {
        // No products found, using sample data
        const sampleProducts = [
          {
            id: '1',
            name: 'Sample Product 1',
            price: 12.99,
            stock: 25,
            imageUrl: 'https://via.placeholder.com/60',
            published: true,
            listingType: 'prepared-meals',
            itemCategory: 'breakfast',
            cuisine: 'american',
            courseType: 'breakfast',
            dishType: 'sandwich',
            description: 'A delicious breakfast item'
          },
          {
            id: '2',
            name: 'Sample Product 2',
            price: 8.50,
            stock: 0,
            imageUrl: 'https://via.placeholder.com/60',
            published: false,
            listingType: 'prepared-meals',
            itemCategory: 'lunch',
            cuisine: 'italian',
            courseType: 'lunch',
            dishType: 'pasta',
            description: 'A tasty lunch option'
          }
        ];
        setProducts(sampleProducts);
      } else {
        setProducts(productsData);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...products];
          // Starting with products

    // Exclude archived products by default (unless explicitly requested)
    if (filters.status !== 'archived') {
      filtered = filtered.filter(product => !product.archived);
      // After archived filter
    }

    // Apply status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(product => {
        if (filters.status === 'active') {
          return product.status === true;
        }
        if (filters.status === 'inactive') {
          return product.status === false;
        }
        if (filters.status === 'archived') {
          return product.archived === true;
        }
        return true;
      });
      // After status filter
    }

    // Apply category filter
    if (filters.category) {
      filtered = filtered.filter(product => 
        product.listingType === filters.category ||
        product.itemCategory === filters.category
      );
      // After category filter
    }

    // Apply subcategory filter
    if (filters.subCategory) {
      filtered = filtered.filter(product => 
        product.cuisine === filters.subCategory ||
        product.courseType === filters.subCategory ||
        product.dishType === filters.subCategory
      );
    }

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(searchLower) ||
        product.description?.toLowerCase().includes(searchLower)
      );
      // After search filter
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'name-desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'price':
          return (a.price || 0) - (b.price || 0);
        case 'price-desc':
          return (b.price || 0) - (a.price || 0);
        case 'date':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [products, filters, sortBy]);

  // Pagination
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedProducts.slice(startIndex, endIndex);
  }, [filteredAndSortedProducts, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedProducts.length / itemsPerPage);

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({
      status: 'all',
      category: '',
      subCategory: '',
      search: ''
    });
    setCurrentPage(1);
  }, []);

  // Get available categories from taxonomy data
  const availableCategories = useMemo(() => {
    // Combine listingType and itemCategory from taxonomy
    const categories = [
      ...taxonomyData.listingType.map(item => item.display),
      ...taxonomyData.itemCategory.map(item => item.display)
    ];
    return categories.sort();
  }, [taxonomyData]);

  // Get available subcategories from taxonomy data
  const availableSubCategories = useMemo(() => {
    // Combine cuisine, courseType, and dishType from taxonomy
    const subCategories = [
      ...taxonomyData.cuisine.map(item => item.display),
      ...taxonomyData.courseType.map(item => item.display),
      ...taxonomyData.dishType.map(item => item.display)
    ];
    return subCategories.sort();
  }, [taxonomyData]);

  // Refresh products
  const refreshProducts = useCallback(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Initial fetch
  useEffect(() => {
    fetchTaxonomyData();
    fetchProducts();
  }, [fetchTaxonomyData, fetchProducts]);

  return {
    // Data
    products: paginatedProducts,
    allProducts: filteredAndSortedProducts,
    totalProducts: filteredAndSortedProducts.length,
    
    // State
    loading,
    error,
    filters,
    sortBy,
    currentPage,
    totalPages,
    itemsPerPage,
    
    // Available options
    availableCategories,
    availableSubCategories,
    taxonomyData,
    
    // Actions
    setSortBy,
    setCurrentPage,
    updateFilters,
    clearFilters,
    refreshProducts,
    
    // Pagination helpers
    hasNextPage: currentPage < totalPages,
    hasPrevPage: currentPage > 1,
    startIndex: (currentPage - 1) * itemsPerPage,
    endIndex: Math.min(currentPage * itemsPerPage, filteredAndSortedProducts.length)
  };
}
